import styled, { css } from 'styled-components'
import Flex from 'styled-flex-component'

import Theme from '@theme'
import { PillButton } from '../PillButton.styled'

// Mirrors the Inbox TitleBar SelectionMode look (see
// src/components/Inbox/TitleBar/SelectionMode/SelectionMode.styled.ts):
// actions on the left, count centered, cancel on the right.
// Wraps on narrow viewports: the action pills alone need ~290px, so at 375px
// the count and cancel move to a second row instead of clipping past the
// bar's right edge (none of the items can shrink — pills are nowrap, the
// count/cancel are content-sized).
export const SelectionBarStyled = styled(Flex)`
  width: 100%;
  flex-wrap: wrap;
  row-gap: 0.5rem;
  padding: 0.75rem 0;
  border-bottom: solid 1px ${({ theme }) => theme.grey400Color};
`

export const SelectionModeActions = styled(Flex)`
  flex-grow: 0;
  flex-shrink: 0;
  gap: 0.5rem;
`

export const SelectionActionButton = styled(PillButton)`
  &:disabled {
    color: ${({ theme }) => theme.grey700Color};
    cursor: auto;
  }
`

export const RejectSelectedButton = styled(SelectionActionButton)`
  &:not(:disabled) {
    border-color: ${({ theme }) => theme.errorColor};
    color: ${({ theme }) => theme.errorColor};
  }
`

export const SelectedCount = styled.div`
  text-align: center;
  flex-grow: 1;
  flex-shrink: 0;
  ${({ hilightCount }: { hilightCount: boolean }) =>
    hilightCount &&
    css`
      .count {
        color: ${Theme.primaryColor};
      }
    `};
`

// Adversarial finding 2: shown under the bar while unloaded pages exist —
// "Select all pending" only reaches the loaded creators, so the hint flags
// that scrolling loads more instead of the count implying completeness.
export const LoadMoreHint = styled.div`
  padding: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey700Color};
  text-align: center;
`

// BRAND-9550: the escalation offer ("All N on this page selected — Select all
// pending in this run"). Deliberately quiet chrome rather than a call to
// action: escalating reaches creators the user has never viewed and undo is
// per-creator only, so it must never be tapped by momentum.
export const SelectAllScopeBanner = styled.div`
  padding: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey900Color};
  text-align: center;
`

export const SelectAllScopeButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 0.8125rem;
  font-weight: 500;
  color: ${({ theme }) => theme.primaryColor};
  text-decoration: underline;
  cursor: pointer;
`

// Shown in place of the banner once escalated — states plainly that the set
// reaches unloaded/unviewed creators and that per-row selection is locked.
export const SelectAllScopeNotice = styled.div`
  padding: 0.5rem 0 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.grey900Color};
  text-align: center;
`

// AC 2 (BRAND-9550): the confirm modal's honesty copy — the set may include
// creators not yet viewed, and undo is per-creator only.
export const WarningText = styled.div`
  padding: 0 0 0.75rem;
  font-size: 0.875rem;
  line-height: 1.4;
  color: ${({ theme }) => theme.grey900Color};
`

// BRAND-9582 (QA item 18): shared TextArea's internal CharacterCounter has
// margin-top but no margin-bottom, so it sits flush against the button row
// below. CharacterCounter isn't exported/configurable and TextArea has ~10
// other consumers, so the gap is added here at the call site instead.
export const CommentTextAreaWrapper = styled.div`
  margin-bottom: 0.75rem;
`

export const CancelButtonWrapper = styled.div`
  flex-grow: 0;

  /* Keeps cancel pinned to the right edge if it ever wraps onto a row
     without the flex-grow count (intermediate widths ~440px). No-op on a
     shared row: the growing count absorbs the free space first. */
  margin-left: auto;
  text-align: right;
`

export const CancelButton = styled.button`
  appearance: none;
  border: none;
  background: transparent;

  /* Buttons keep UA padding (1px 6px) even after appearance: none — zero it
     so the label right-aligns flush like the Inbox bar's span-based cancel. */
  padding: 0;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.grey900Color};
  cursor: pointer;
`
